import type { ApiResponse } from '../../types'
import type {
  GroupSummaryDto,
  GroupDetailDto,
  CreateGroupRequest,
  SaveGroupProfileRequest,
  GroupProfileDto,
  BrowseParams,
  BrowseResultDto,
  CircleSummaryDto,
} from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_Groups. Paths are relative to `${VITE_API_URL}/api/v1/community`.

/** A pending host-facing vouch request on a group the caller hosts. */
export interface GroupJoinRequestDto {
  id: string
  familyName: string
  circleNames: string
  whenText: string
}

async function get<T>(path: string): Promise<T> {
  try {
    const res = await client.get<ApiResponse<T>>(path)
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

async function send<T>(method: 'post' | 'put' | 'delete', path: string, body?: unknown): Promise<T> {
  try {
    const res = await client.request<ApiResponse<T>>({ method, url: path, data: body })
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /groups — sidebar summaries for the caller's groups. */
export function listGroups(): Promise<GroupSummaryDto[]> {
  return get<GroupSummaryDto[]>('/groups')
}

/** GET /groups/my-circles — the caller's own circles for the create/join multi-select. */
export function getMyComposableCircles(): Promise<CircleSummaryDto[]> {
  return get<CircleSummaryDto[]>('/groups/my-circles')
}

/** GET /groups/{id} — full group detail. */
export function getGroup(id: string): Promise<GroupDetailDto> {
  return get<GroupDetailDto>(`/groups/${id}`)
}

/** POST /groups — create (creator family = Host). */
export function createGroup(req: CreateGroupRequest): Promise<GroupSummaryDto> {
  return send<GroupSummaryDto>('post', '/groups', req)
}

/** POST /groups/{id}/circles/{circleId} — attach a circle (host only). */
export function attachCircle(id: string, circleId: string): Promise<void> {
  return send<void>('post', `/groups/${id}/circles/${circleId}`)
}

/** DELETE /groups/{id}/circles/{circleId} — detach a circle (host only). */
export function detachCircle(id: string, circleId: string): Promise<void> {
  return send<void>('delete', `/groups/${id}/circles/${circleId}`)
}

/** POST /groups/{id}/leave. */
export function leaveGroup(id: string): Promise<void> {
  return send<void>('post', `/groups/${id}/leave`)
}

/** PUT /groups/{id}/profile — upsert the acting person's group profile. */
export function saveProfile(id: string, req: SaveGroupProfileRequest): Promise<GroupProfileDto> {
  return send<GroupProfileDto>('put', `/groups/${id}/profile`, req)
}

/** GET /groups/browse — discovery across the network. */
export function browseGroups(params: BrowseParams = {}): Promise<BrowseResultDto[]> {
  const qs = new URLSearchParams()
  if (params.query) qs.set('query', params.query)
  if (params.game) qs.set('game', params.game)
  if (params.style) qs.set('style', params.style)
  if (params.sort) qs.set('sort', params.sort)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return get<BrowseResultDto[]>(`/groups/browse${suffix}`)
}

/** POST /groups/{id}/join — parent joins by bringing circles. */
export function joinGroup(id: string, circleIds: string[]): Promise<void> {
  return send<void>('post', `/groups/${id}/join`, { circleIds })
}

/** GET /groups/{id}/join-requests — pending host-facing vouch requests. */
export function listJoinRequests(id: string): Promise<GroupJoinRequestDto[]> {
  return get<GroupJoinRequestDto[]>(`/groups/${id}/join-requests`)
}

/** POST /groups/{id}/join-requests/{reqId}/approve — host approves (attaches). */
export function approveJoinRequest(id: string, reqId: string): Promise<void> {
  return send<void>('post', `/groups/${id}/join-requests/${reqId}/approve`)
}

/** POST /groups/{id}/join-requests/{reqId}/decline — host declines (silent). */
export function declineJoinRequest(id: string, reqId: string): Promise<void> {
  return send<void>('post', `/groups/${id}/join-requests/${reqId}/decline`)
}

/** POST /groups/{id}/join-asks — kid-initiated ask (routes to guardian). */
export function askToJoinGroup(id: string): Promise<void> {
  return send<void>('post', `/groups/${id}/join-asks`, {})
}

// ── COTC_GroupsModelRevision: per-person invitations, circle fan-out, guardian observation ──

/** A pending group invitation (COTC_GroupsModelRevision R2/R9). */
export interface GroupInvitationDto {
  id: string
  groupId: string
  groupName: string
  game: string
  tint: string
  viaCircleName: string
  invitedByFirstName: string
  forPersonId: string
  forFirstName: string
  /** The invited person is a managed child — a guardian gates acceptance. */
  needsGuardian: boolean
  whenText: string
}

/** A guardian's read-only observation of a member child's group (R8). */
export interface GroupObservationDto {
  groupId: string
  groupName: string
  game: string
  observedChildFirstName: string
  members: GroupDetailDto['members']
}

/** POST /groups/{id}/invite-circle/{circleId} — fan out invitations (host only). Returns the count invited. */
export async function inviteCircle(id: string, circleId: string): Promise<number> {
  const res = await send<{ invited: number }>('post', `/groups/${id}/invite-circle/${circleId}`)
  return res?.invited ?? 0
}

/** GET /groups/invitations — the acting person's (and, for a guardian, their children's) invitations. */
export function listInvitations(): Promise<GroupInvitationDto[]> {
  return get<GroupInvitationDto[]>('/groups/invitations')
}

/** POST /groups/{id}/invitations/{invId}/accept — adult → member; child → guardian approval. */
export function acceptInvitation(id: string, invitationId: string): Promise<void> {
  return send<void>('post', `/groups/${id}/invitations/${invitationId}/accept`)
}

/** POST /groups/{id}/invitations/{invId}/decline. */
export function declineInvitation(id: string, invitationId: string): Promise<void> {
  return send<void>('post', `/groups/${id}/invitations/${invitationId}/decline`)
}

/** GET /groups/{id}/observe — guardian read-only observation of a member child's group (R8). */
export function observeGroup(id: string): Promise<GroupObservationDto> {
  return get<GroupObservationDto>(`/groups/${id}/observe`)
}
